import { pickAppCopy, type SupportedLocale } from "@patient-watch/i18n";
import { useMemo } from "react";
import { useAppLocale } from "./LocaleContext";
import {
    MS_ADL_ACTIVITY_KEYS,
    msAdlCopyEn,
    type MsAdlActivityKey,
    type MsAdlActivityLabels,
} from "@patient-watch/i18n/webapp/en-GB/msAdlCopy";
import { msAdlCopyDe } from "@patient-watch/i18n/webapp/de-DE/msAdlCopy";
import { msAdlCopyDa } from "@patient-watch/i18n/webapp/da-DK/msAdlCopy";
import { msAdlCopyEnUs } from "@patient-watch/i18n/webapp/en-US/msAdlCopy";
import { msAdlCopyFr } from "@patient-watch/i18n/webapp/fr-FR/msAdlCopy";
import { msAdlCopyIt } from "@patient-watch/i18n/webapp/it-IT/msAdlCopy";
import { msAdlCopyEs } from "@patient-watch/i18n/webapp/es-ES/msAdlCopy";

export type { MsAdlActivityKey, MsAdlActivityLabels };
export { MS_ADL_ACTIVITY_KEYS };

interface MsAdlCopy {
    activities: MsAdlActivityLabels;
    formatActivity: (activity: string) => string;
}

const msAdlCopyByLocale: Record<SupportedLocale, MsAdlActivityLabels> = {
    "en-GB": msAdlCopyEn,
    "en-US": msAdlCopyEnUs,
    "de-DE": msAdlCopyDe,
    "da-DK": msAdlCopyDa,
    "fr-FR": msAdlCopyFr,
    "it-IT": msAdlCopyIt,
    "es-ES": msAdlCopyEs,
};

function isMsAdlActivityKey(activity: string): activity is MsAdlActivityKey {
    return (MS_ADL_ACTIVITY_KEYS as readonly string[]).includes(activity);
}

export function formatMsAdlActivity(activity: string, locale: SupportedLocale): string {
    const labels = pickAppCopy(msAdlCopyByLocale, locale);
    if (isMsAdlActivityKey(activity)) {
        return labels[activity];
    }
    return activity;
}

function buildMsAdlCopy(locale: SupportedLocale): MsAdlCopy {
    const activities = pickAppCopy(msAdlCopyByLocale, locale);
    return {
        activities,
        formatActivity: (activity) => formatMsAdlActivity(activity, locale),
    };
}

const msAdlCopyCache: Record<SupportedLocale, MsAdlCopy> = {
    "en-GB": buildMsAdlCopy("en-GB"),
    "en-US": buildMsAdlCopy("en-US"),
    "de-DE": buildMsAdlCopy("de-DE"),
    "da-DK": buildMsAdlCopy("da-DK"),
    "fr-FR": buildMsAdlCopy("fr-FR"),
    "it-IT": buildMsAdlCopy("it-IT"),
    "es-ES": buildMsAdlCopy("es-ES"),
};

export function useMsAdlCopy(): MsAdlCopy {
    const { locale } = useAppLocale();
    return useMemo(() => pickAppCopy(msAdlCopyCache, locale), [locale]);
}

export function getMsAdlCopy(locale: SupportedLocale): MsAdlCopy {
    return pickAppCopy(msAdlCopyCache, locale);
}
